import React, { useEffect, useState } from 'react';
import { Typography, Spin, Alert } from 'antd';
import { IconPlugConnectedX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getExhaustedErpSyncEntries, IErpSyncOutboxEntry } from '../../../services/AdminService';
import EmptyState from '../../../components/EmptyState';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, numberSorter, dateSorter, uniqueValueFilters, stringOnFilter } from '../../../helpers/adminTableHelpers';

const { Title, Text } = Typography;

// Read-only: ErpSyncOutbox rows that exhausted ErpSyncBackgroundService's
// 8 automatic retries (Status="Failed", NextRetryAt=null -- see that
// entity's own doc comment) and stopped being picked up automatically.
// Nothing to retry/edit/delete from this screen yet -- this is purely so
// an admin can notice a given host's ERP sync is broken and go investigate
// it manually (check the host's WebhookUrl/WebhookSecret, their endpoint's
// own logs, etc.).
const ErpSyncSection: React.FC = () => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<IErpSyncOutboxEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    getExhaustedErpSyncEntries()
      .then(setEntries)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const hostFilters = uniqueValueFilters(entries, 'hostUserId');
  const eventTypeFilters = uniqueValueFilters(entries, 'eventType');

  return (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_ERP_SYNC)}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        {translate(t, TRANSLATIONS.ADMIN_ERP_SYNC_DESCRIPTION)}
      </Text>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_ERP_SYNC_LOAD_ERROR)} />
      ) : entries.length === 0 ? (
        <EmptyState
          icon={<IconPlugConnectedX size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_ERP_SYNC_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_ERP_SYNC_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IErpSyncOutboxEntry>
          rowKey="id"
          dataSource={entries}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(record) => ({
            key: String(record.id),
            title: record.eventType,
            subtitle: record.hostUserId,
            fields: [
              { label: translate(t, TRANSLATIONS.ADMIN_ERP_SYNC_COL_ATTEMPTS), value: record.attempts },
              { label: translate(t, TRANSLATIONS.ADMIN_ERP_SYNC_COL_CREATED_AT), value: new Date(record.createdAt).toLocaleString() },
              // The error message is the whole point of this screen, so it
              // gets the full width rather than half of a phone.
              { label: translate(t, TRANSLATIONS.ADMIN_ERP_SYNC_COL_ERROR), value: record.lastErrorMessage ?? '—', wide: true },
            ],
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_ERP_SYNC_COL_HOST),
              dataIndex: 'hostUserId',
              key: 'hostUserId',
              sorter: textSorter<IErpSyncOutboxEntry>('hostUserId'),
              filters: hostFilters,
              onFilter: stringOnFilter<IErpSyncOutboxEntry>('hostUserId'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_ERP_SYNC_COL_EVENT_TYPE),
              dataIndex: 'eventType',
              key: 'eventType',
              sorter: textSorter<IErpSyncOutboxEntry>('eventType'),
              filters: eventTypeFilters,
              onFilter: stringOnFilter<IErpSyncOutboxEntry>('eventType'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_ERP_SYNC_COL_ATTEMPTS),
              dataIndex: 'attempts',
              key: 'attempts',
              sorter: numberSorter<IErpSyncOutboxEntry>('attempts'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_ERP_SYNC_COL_ERROR),
              dataIndex: 'lastErrorMessage',
              key: 'lastErrorMessage',
              render: (value?: string) => value ?? '—',
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_ERP_SYNC_COL_CREATED_AT),
              dataIndex: 'createdAt',
              key: 'createdAt',
              sorter: dateSorter<IErpSyncOutboxEntry>('createdAt'),
              defaultSortOrder: 'descend',
              render: (value: string) => new Date(value).toLocaleString(),
            },
          ]}
        />
      )}
    </div>
  );
};

export default ErpSyncSection;
